/**
 * Darstellung der OAuth2-Anbieter (Logo + Beschriftung).
 *
 * Die Anbieter kommen aus PocketBase (`listAuthMethods`) – hier steht nur,
 * wie sie aussehen. Unbekannte Anbieter (inkl. generischer OIDC-Slots
 * `oidc`, `oidc2`, `oidc3`) bekommen ein neutrales Schlüssel-Icon und den
 * in PocketBase gepflegten Anzeigenamen.
 */

export interface OAuthBrand {
  /** Beschriftung des Buttons, z. B. „Weiter mit Google". */
  label: string;
  /** Inline-SVG des Logos. */
  svg: string;
  /** Zusatzklasse für markentypische Farben (z. B. schwarzes Apple-Logo). */
  className: string;
}

/** Mehrfarbiges Google-„G". */
const GOOGLE = `<svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/></svg>`;

/** Einfarbige Logos übernehmen die Textfarbe (funktioniert in Hell + Dunkel). */
const APPLE = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.36 12.72c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.83-.81-3.01-.79-1.55.02-2.98.9-3.77 2.28-1.61 2.8-.41 6.94 1.15 9.21.77 1.11 1.68 2.36 2.87 2.31 1.15-.05 1.58-.74 2.98-.74 1.39 0 1.78.74 3 .72 1.24-.02 2.02-1.13 2.78-2.24.87-1.29 1.23-2.54 1.25-2.6-.03-.01-2.39-.92-2.41-3.72zM14.1 5.6c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.67 1.37-.58.68-1.09 1.77-.95 2.81 1.02.08 2.06-.52 2.68-1.28z"/></svg>`;

const MICROSOFT = `<svg width="18" height="18" viewBox="0 0 23 23" aria-hidden="true"><path fill="#F25022" d="M0 0h11v11H0z"/><path fill="#7FBA00" d="M12 0h11v11H12z"/><path fill="#00A4EF" d="M0 12h11v11H0z"/><path fill="#FFB900" d="M12 12h11v11H12z"/></svg>`;

const GITHUB = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 .5A11.5 11.5 0 0 0 .5 12a11.5 11.5 0 0 0 7.86 10.92c.58.1.79-.25.79-.56v-2c-3.2.7-3.88-1.37-3.88-1.37-.53-1.34-1.29-1.7-1.29-1.7-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.2 1.77 1.2 1.03 1.77 2.7 1.26 3.36.96.1-.75.4-1.26.73-1.55-2.56-.29-5.25-1.28-5.25-5.7 0-1.26.45-2.29 1.19-3.1-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.79 0c2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.12 3.05.74.81 1.18 1.84 1.18 3.1 0 4.43-2.69 5.4-5.26 5.69.41.36.78 1.06.78 2.15v3.19c0 .31.21.67.8.56A11.5 11.5 0 0 0 23.5 12 11.5 11.5 0 0 0 12 .5z"/></svg>`;

const GITLAB = `<svg width="18" height="18" viewBox="0 0 24 24" fill="#FC6D26" aria-hidden="true"><path d="m12 22-4-12.4H16zM12 22 2.6 9.6h5.5zM2.6 9.6 1.2 14a1 1 0 0 0 .36 1.1L12 22zM2.6 9.6h5.5L5.7 2.3c-.12-.37-.65-.37-.77 0zM12 22l4-12.4h5.4zM21.4 9.6l1.4 4.4a1 1 0 0 1-.36 1.1L12 22zM21.4 9.6h-5.4l2.3-7.3c.12-.37.65-.37.77 0z"/></svg>`;

const DISCORD = `<svg width="18" height="18" viewBox="0 0 24 24" fill="#5865F2" aria-hidden="true"><path d="M20.3 4.5A19.8 19.8 0 0 0 15.4 3l-.25.5c1.6.4 2.9 1 4.2 1.9a13.9 13.9 0 0 0-14.7 0c1.3-.9 2.8-1.6 4.2-1.9L8.6 3a19.8 19.8 0 0 0-4.9 1.5C1.1 8.4.4 12.2.7 15.9a19.9 19.9 0 0 0 6 3.1l.7-1.1c-.7-.3-1.4-.6-2-1l.4-.3a14.2 14.2 0 0 0 12.3 0l.4.3c-.6.4-1.3.7-2 1l.7 1.1c2.2-.7 4.2-1.7 6-3.1.4-4.3-.7-8.1-2.9-11.4zM8.7 13.9c-1.2 0-2.1-1.1-2.1-2.4S7.5 9.1 8.7 9.1s2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4zm6.6 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4z"/></svg>`;

/** Neutrales Schlüssel-Icon für OIDC & unbekannte Anbieter. */
const GENERIC = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 2l-2 2m-7.6 7.6a5 5 0 1 1-7.1 7.1 5 5 0 0 1 7.1-7.1zm0 0L15.5 7.5m0 0 3 3L22 7l-3-3"/></svg>`;

const BRANDS: Record<string, { name: string; svg: string; className?: string }> = {
  google: { name: "Google", svg: GOOGLE },
  apple: { name: "Apple", svg: APPLE, className: "oauth-mono" },
  microsoft: { name: "Microsoft", svg: MICROSOFT },
  github: { name: "GitHub", svg: GITHUB, className: "oauth-mono" },
  gitlab: { name: "GitLab", svg: GITLAB },
  discord: { name: "Discord", svg: DISCORD },
};

/** Generische OIDC-Slots in PocketBase heißen oidc, oidc2, oidc3. */
const OIDC = /^oidc\d*$/;

export function oauthBrand(provider: { name: string; displayName?: string }): OAuthBrand {
  const key = provider.name.toLowerCase();
  const brand = BRANDS[key];
  if (brand) {
    return {
      label: `Weiter mit ${brand.name}`,
      svg: brand.svg,
      className: brand.className ?? "",
    };
  }
  // OIDC ohne gepflegten Namen: neutrale, verständliche Beschriftung.
  const fallback = OIDC.test(key) ? "Single Sign-On" : provider.name;
  return {
    label: `Weiter mit ${provider.displayName?.trim() || fallback}`,
    svg: GENERIC,
    className: "oauth-mono",
  };
}
